import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeftIcon, ChevronRightIcon, PlusIcon } from '@heroicons/react/24/outline';
import { useBookings, Booking } from '../../stores/useBookingStore';
import { useCurrentStore } from '../../hooks/useCurrentStore';
import BookingDialog from '../../components/BookingDialog';
import { useServiceStore } from '../../stores/useServiceStore';
import { useStaffStore } from '../../stores/useStaffStore';
import { useCalendarStore } from '../../stores/useCalendarStore';


const CalendarPage: React.FC = () => {
  const { t } = useTranslation(['pages', 'translation']);
  const { storeId } = useCurrentStore();
  const { createBooking, updateBooking } = useBookings();
  const fetchServices = useServiceStore((state) => state.fetchServices);
  const fetchStaff = useStaffStore((state) => state.fetchStaff);
  const staff = useStaffStore((state) => state.staff);

  // ✅ 使用 Zustand persist store（與其他頁面一致的快取模式）
  const {
    calendarBookings,
    isLoading: isLoadingCalendar,
    hasFetched,
    viewMode,
    selectedDate: selectedDateStr,
    fetchCalendarData,
    setViewMode,
    setSelectedDate: setSelectedDateStore,
    refreshCalendarData,
  } = useCalendarStore();

  // 將 ISO string 轉換為 Date 物件
  const selectedDate = new Date(selectedDateStr);

  const [showDialog, setShowDialog] = useState(false);
  const [prefilledDate, setPrefilledDate] = useState<Date | null>(null);
  const [editingBooking, setEditingBooking] = useState<Booking | null>(null);

  // ✅ 資料載入邏輯（使用 store 的 fetchCalendarData）
  useEffect(() => {
    if (!storeId) return;

    fetchServices(storeId);
    fetchStaff(storeId);
  }, [storeId, fetchServices, fetchStaff]);

  // 切換日期時，若超出快取涵蓋範圍才重新載入（fetchCalendarData 內部判斷）
  useEffect(() => {
    if (!storeId) return;
    fetchCalendarData(storeId, new Date(selectedDateStr));
  }, [storeId, selectedDateStr, fetchCalendarData]);

  // 獲取週範圍（週日到週六）
  const getWeekRange = (date: Date) => {
    const day = date.getDay(); // 0 = 週日, 1 = 週一...
    const diff = -day; // 調整到週日（當週的第一天）
    const sunday = new Date(date);
    sunday.setDate(date.getDate() + diff);
    sunday.setHours(0, 0, 0, 0);

    const days = [];
    for (let i = 0; i < 7; i++) {
      const day = new Date(sunday);
      day.setDate(sunday.getDate() + i);
      days.push(day);
    }
    return days;
  };

  const weekDays = getWeekRange(selectedDate);

  // 過濾當日預約（使用 calendarBookings 而非 bookings）
  const todayBookings = (calendarBookings || []).filter((booking) => {
    const bookingDate = new Date(booking.date);
    return (
      bookingDate.getFullYear() === selectedDate.getFullYear() &&
      bookingDate.getMonth() === selectedDate.getMonth() &&
      bookingDate.getDate() === selectedDate.getDate() &&
      booking.status !== 'CANCELLED'
    );
  });

  // 過濾本週預約
  const weekBookings = (calendarBookings || []).filter((booking) => {
    const bookingDate = new Date(booking.date);
    return weekDays.some(day =>
      bookingDate.getFullYear() === day.getFullYear() &&
      bookingDate.getMonth() === day.getMonth() &&
      bookingDate.getDate() === day.getDate()
    ) && booking.status !== 'CANCELLED';
  });

  // 依時間排序
  const sortedBookings = [...todayBookings].sort((a, b) =>
    new Date(a.date).getTime() - new Date(b.date).getTime()
  );

  // 生成時間槽（9:00 - 21:00，每30分鐘一個槽）
  const generateTimeSlots = () => {
    const slots: { time: string; hour: number; minute: number }[] = [];
    for (let hour = 9; hour < 21; hour++) {
      for (let minute of [0, 30]) {
        slots.push({
          time: `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`,
          hour,
          minute,
        });
      }
    }
    return slots;
  };

  const timeSlots = generateTimeSlots();

  const getBookingForSlot = (hour: number, minute: number) => {
    return sortedBookings.find((booking) => {
      const bookingDate = new Date(booking.date);
      return bookingDate.getHours() === hour && bookingDate.getMinutes() === minute;
    });
  };

  const handlePrevDay = () => {
    const newDate = new Date(selectedDate);
    if (viewMode === 'week') {
      // 週視圖：切換整週（7天）
      newDate.setDate(newDate.getDate() - 7);
    } else {
      // 日視圖/多員工視圖：切換一天
      newDate.setDate(newDate.getDate() - 1);
    }
    setSelectedDateStore(newDate);
  };

  const handleNextDay = () => {
    const newDate = new Date(selectedDate);
    if (viewMode === 'week') {
      // 週視圖：切換整週（7天）
      newDate.setDate(newDate.getDate() + 7);
    } else {
      // 日視圖/多員工視圖：切換一天
      newDate.setDate(newDate.getDate() + 1);
    }
    setSelectedDateStore(newDate);
  };

  const handleToday = () => {
    setSelectedDateStore(new Date());
  };

  const handleAddBooking = (hour?: number, minute?: number) => {
    if (hour !== undefined && minute !== undefined) {
      const newDate = new Date(selectedDate);
      newDate.setHours(hour, minute, 0, 0);
      setPrefilledDate(newDate);
    } else {
      setPrefilledDate(null);
    }
    setEditingBooking(null);
    setShowDialog(true);
  };

  const handleEditBooking = (booking: Booking) => {
    setEditingBooking(booking);
    setPrefilledDate(null);
    setShowDialog(true);
  };

  const handleDialogClose = () => {
    setShowDialog(false);
    setPrefilledDate(null);
    setEditingBooking(null);
  };

  const handleDialogSubmit = async (data: any) => {
    if (!storeId) return;

    if (editingBooking) {
      await updateBooking(editingBooking.id, data);
    } else {
      await createBooking(storeId, data);
    }

    handleDialogClose();

    // 使用 store 的 refreshCalendarData 重新載入資料
    await refreshCalendarData(storeId, selectedDate);
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      PENDING: 'bg-yellow-100 border-yellow-300 dark:bg-yellow-900/20 dark:border-yellow-600',
      CONFIRMED: 'bg-blue-100 border-blue-300 dark:bg-blue-900/20 dark:border-blue-600',
      COMPLETED: 'bg-green-100 border-green-300 dark:bg-green-900/20 dark:border-green-600',
    };
    return colors[status] || 'bg-gray-100 border-gray-300 dark:bg-slate-700 dark:border-slate-600';
  };

  const formatDate = (date: Date) => {
    const weekdays = ['日', '一', '二', '三', '四', '五', '六'];
    return `${date.getFullYear()}/${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getDate()).padStart(2, '0')} (${weekdays[date.getDay()]})`;
  };

  // 只在首次無資料且尚未讀取過時顯示載入畫面
  // hasFetched 確保「讀取後無資料」的情況不會重複顯示載入
  if (isLoadingCalendar && calendarBookings.length === 0 && !hasFetched) {
    return (
      <div className="container mx-auto px-4 md:px-10 py-6 pb-32">
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">
          {t('translation:common.loading')}
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 md:px-10 py-6 pb-32">
      {/* 標題列 + 視圖切換 */}
      <div className="mb-4">
        <div className="flex flex-col md:flex-row md:items-baseline md:justify-between gap-3">
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
            {t('pages:admin.calendar.title')}
          </h1>
          <div className="flex gap-2">
            <button
              onClick={() => setViewMode('day')}
              className={`px-4 py-2 rounded font-medium transition-colors ${
                viewMode === 'day'
                  ? 'bg-brand-600 text-white'
                  : 'bg-gray-200 dark:bg-slate-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-slate-600'
              }`}
            >
              {t('pages:admin.calendar.dayView')}
            </button>
            <button
              onClick={() => setViewMode('week')}
              className={`px-4 py-2 rounded font-medium transition-colors ${
                viewMode === 'week'
                  ? 'bg-brand-600 text-white'
                  : 'bg-gray-200 dark:bg-slate-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-slate-600'
              }`}
            >
              {t('pages:admin.calendar.weekView')}
            </button>
            <button
              onClick={() => setViewMode('staff')}
              className={`px-4 py-2 rounded font-medium transition-colors ${
                viewMode === 'staff'
                  ? 'bg-brand-600 text-white'
                  : 'bg-gray-200 dark:bg-slate-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-slate-600'
              }`}
            >
              {t('pages:admin.calendar.staffView')}
            </button>
          </div>
        </div>
      </div>

      {/* 日期選擇器 */}
      <div className="mb-6 flex items-center justify-between bg-white dark:bg-slate-800 shadow rounded-lg p-4">
        <button
          onClick={handlePrevDay}
          className="p-2 hover:bg-gray-100 dark:hover:bg-slate-700 rounded transition-colors"
          aria-label="上一天"
        >
          <ChevronLeftIcon className="h-6 w-6 text-gray-600 dark:text-gray-400" />
        </button>

        <div className="flex items-center space-x-3">
          <span className="text-lg font-semibold text-gray-900 dark:text-white">
            {formatDate(selectedDate)}
          </span>
          <button
            onClick={handleToday}
            className="px-3 py-1 bg-brand-100 dark:bg-brand-900/30 text-brand-600 dark:text-brand-400 text-sm font-medium rounded hover:bg-brand-200 dark:hover:bg-brand-900/50 transition-colors"
          >
            {viewMode === 'week' ? t('pages:admin.calendar.thisWeek') : t('pages:admin.calendar.today')}
          </button>
        </div>

        <button
          onClick={handleNextDay}
          className="p-2 hover:bg-gray-100 dark:hover:bg-slate-700 rounded transition-colors"
          aria-label="下一天"
        >
          <ChevronRightIcon className="h-6 w-6 text-gray-600 dark:text-gray-400" />
        </button>
      </div>

      {/* 統計資訊 */}
      <div className="grid grid-cols-2 gap-3 md:gap-4 mb-6">
        <div className="bg-white dark:bg-slate-800 shadow rounded-lg p-3 md:p-4">
          <p className="text-xs md:text-sm text-gray-500 dark:text-gray-400 truncate">{t('pages:admin.calendar.totalBookings')}</p>
          <p className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white mt-1">
            {viewMode === 'week' ? weekBookings.length : todayBookings.length}
          </p>
        </div>
        <div className="bg-white dark:bg-slate-800 shadow rounded-lg p-3 md:p-4">
          <p className="text-xs md:text-sm text-gray-500 dark:text-gray-400 truncate">{t('translation:bookingStatus.PENDING')}</p>
          <p className="text-xl md:text-2xl font-bold text-yellow-600 dark:text-yellow-400 mt-1">
            {viewMode === 'week' ? weekBookings.filter(b => b.status === 'PENDING').length : todayBookings.filter(b => b.status === 'PENDING').length}
          </p>
        </div>
        <div className="bg-white dark:bg-slate-800 shadow rounded-lg p-3 md:p-4">
          <p className="text-xs md:text-sm text-gray-500 dark:text-gray-400 truncate">{t('translation:bookingStatus.CONFIRMED')}</p>
          <p className="text-xl md:text-2xl font-bold text-blue-600 dark:text-blue-400 mt-1">
            {viewMode === 'week' ? weekBookings.filter(b => b.status === 'CONFIRMED').length : todayBookings.filter(b => b.status === 'CONFIRMED').length}
          </p>
        </div>
        <div className="bg-white dark:bg-slate-800 shadow rounded-lg p-3 md:p-4">
          <p className="text-xs md:text-sm text-gray-500 dark:text-gray-400 truncate">{t('translation:bookingStatus.COMPLETED')}</p>
          <p className="text-xl md:text-2xl font-bold text-green-600 dark:text-green-400 mt-1">
            {viewMode === 'week' ? weekBookings.filter(b => b.status === 'COMPLETED').length : todayBookings.filter(b => b.status === 'COMPLETED').length}
          </p>
        </div>
      </div>

      {/* 日視圖 */}
      {viewMode === 'day' && (
        <div className="bg-white dark:bg-slate-800 shadow rounded-lg overflow-hidden">
          <div className="p-4 border-b border-gray-200 dark:border-slate-700">
            <h2 className="font-semibold text-gray-900 dark:text-white">
              {t('pages:admin.calendar.bookingSchedule')}
            </h2>
          </div>
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {timeSlots.map((slot) => {
              const booking = getBookingForSlot(slot.hour, slot.minute);

              return (
                <div
                  key={slot.time}
                  className="flex items-stretch hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors"
                >
                  {/* 時間標籤 */}
                  <div className="w-20 md:w-24 flex-shrink-0 p-3 md:p-4 border-r border-gray-200 dark:border-slate-700">
                    <span className="text-sm md:text-base font-medium text-gray-600 dark:text-gray-400">
                      {slot.time}
                    </span>
                  </div>

                  {/* 預約內容 */}
                  <div className="flex-1 p-3 md:p-4">
                    {booking ? (
                      <button
                        onClick={() => handleEditBooking(booking)}
                        className={`w-full text-left border-l-4 pl-3 md:pl-4 py-2 ${getStatusColor(booking.status)} hover:shadow-md transition-shadow cursor-pointer`}
                      >
                        <div className="flex items-start gap-3">
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-gray-900 dark:text-white truncate">
                              {booking.customer.name}
                            </p>
                            <div className="flex items-center gap-2 mt-1">
                              <p className="text-sm text-gray-600 dark:text-gray-400 truncate">
                                {booking.service.name}
                              </p>
                              <span className="text-sm font-bold text-brand-600 dark:text-brand-400 flex-shrink-0">
                                ${Math.floor(Number(booking.price))}
                              </span>
                            </div>
                            {booking.staff && (
                              <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                                {t('translation:common.staffMember')}：{booking.staff.name}
                              </p>
                            )}
                          </div>
                        </div>
                      </button>
                    ) : (
                      <button
                        onClick={() => handleAddBooking(slot.hour, slot.minute)}
                        className="w-full h-full flex items-center justify-center text-gray-400 dark:text-gray-600 hover:text-brand-600 dark:hover:text-brand-400 hover:bg-brand-50 dark:hover:bg-brand-900/20 transition-all group"
                      >
                        <PlusIcon className="w-5 h-5 group-hover:scale-110 transition-transform" />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* 週視圖 */}
      {viewMode === 'week' && (
        <div className="bg-white dark:bg-slate-800 shadow rounded-lg overflow-hidden">
          {/* 手機版：按日期分組顯示本週預約 */}
          <div className="md:hidden">
            {weekDays.map((day) => {
              const weekdayNames = ['日', '一', '二', '三', '四', '五', '六'];
              const isToday = day.toDateString() === new Date().toDateString();
              const dayBookings = weekBookings.filter(b => {
                const bDate = new Date(b.date);
                return bDate.getFullYear() === day.getFullYear() &&
                       bDate.getMonth() === day.getMonth() &&
                       bDate.getDate() === day.getDate();
              }).sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

              return (
                <div key={day.toISOString()} className="border-b border-gray-200 dark:border-slate-700 last:border-0">
                  <div className={`p-4 ${isToday ? 'bg-brand-50 dark:bg-brand-900/20' : 'bg-gray-50 dark:bg-slate-700/50'}`}>
                    <h3 className={`font-semibold ${isToday ? 'text-brand-600 dark:text-brand-400' : 'text-gray-900 dark:text-white'}`}>
                      {day.getMonth() + 1}/{day.getDate()} ({weekdayNames[day.getDay()]})
                      <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">
                        {t('pages:admin.calendar.bookingCount', { count: dayBookings.length })}
                      </span>
                      {isToday && <span className="ml-2 text-xs bg-brand-600 text-white px-2 py-0.5 rounded">{t('pages:admin.calendar.today')}</span>}
                    </h3>
                  </div>
                  {dayBookings.length === 0 ? (
                    <div className="p-4 text-center text-sm text-gray-500 dark:text-gray-400">
                      {t('pages:admin.calendar.noBookingsOnThisDay')}
                    </div>
                  ) : (
                    <div className="divide-y divide-gray-200 dark:divide-gray-700">
                      {dayBookings.map((booking) => (
                        <button
                          key={booking.id}
                          onClick={() => handleEditBooking(booking)}
                          className={`w-full text-left p-3 hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors ${getStatusColor(booking.status)} border-l-4`}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
                                  {new Date(booking.date).toLocaleTimeString('zh-TW', { hour: '2-digit', minute: '2-digit' })}
                                </span>
                                <span className="text-xs font-bold text-brand-600 dark:text-brand-400">
                                  ${Math.floor(Number(booking.price))}
                                </span>
                              </div>
                              <p className="font-semibold text-sm text-gray-900 dark:text-white mt-1 truncate">
                                {booking.customer.name}
                              </p>
                              <p className="text-xs text-gray-600 dark:text-gray-400 truncate">
                                {booking.service.name}
                              </p>
                              {booking.staff && (
                                <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                                  {booking.staff.name}
                                </p>
                              )}
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* 桌面版：顯示完整週視圖表格 */}
          <div className="hidden md:block overflow-x-auto scrollbar-hide">
            <table className="w-full" style={{ minWidth: '800px' }}>
              <thead>
                <tr className="border-b border-gray-200 dark:border-slate-700">
                  <th className="p-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 w-20">{t('translation:common.timeLabel')}</th>
                  {weekDays.map((day, idx) => {
                    const weekdayNames = ['日', '一', '二', '三', '四', '五', '六'];
                    const isToday = day.toDateString() === new Date().toDateString();
                    return (
                      <th key={idx} className={`p-3 text-center text-xs font-medium ${isToday ? 'bg-brand-50 dark:bg-brand-900/20' : ''}`}>
                        <div className={`${isToday ? 'text-brand-600 dark:text-brand-400 font-bold' : 'text-gray-500 dark:text-gray-400'}`}>
                          <div>{weekdayNames[day.getDay()]}</div>
                          <div className="text-lg mt-1">{day.getDate()}</div>
                        </div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {timeSlots.map((slot) => (
                  <tr key={slot.time} className="hover:bg-gray-50 dark:hover:bg-slate-700/50">
                    <td className="p-2 text-xs text-gray-600 dark:text-gray-400 font-medium border-r border-gray-200 dark:border-slate-700">
                      {slot.time}
                    </td>
                    {weekDays.map((day, dayIdx) => {
                      const dayBookings = weekBookings.filter(b => {
                        const bDate = new Date(b.date);
                        return bDate.getFullYear() === day.getFullYear() &&
                               bDate.getMonth() === day.getMonth() &&
                               bDate.getDate() === day.getDate() &&
                               bDate.getHours() === slot.hour &&
                               bDate.getMinutes() === slot.minute;
                      });
                      const booking = dayBookings[0];
                      const isToday = day.toDateString() === new Date().toDateString();

                      return (
                        <td key={dayIdx} className={`p-1 text-center ${isToday ? 'bg-brand-50/50 dark:bg-brand-900/10' : ''}`}>
                          {booking ? (
                            <button
                              onClick={() => handleEditBooking(booking)}
                              className={`w-full text-xs p-2 rounded ${getStatusColor(booking.status)} hover:shadow-md transition-shadow cursor-pointer`}
                              title={`${booking.customer.name} - ${booking.service.name}`}
                            >
                              <div className="truncate font-semibold">{booking.customer.name}</div>
                              <div className="truncate text-xs opacity-75">${Math.floor(Number(booking.price))}</div>
                            </button>
                          ) : (
                            <button
                              onClick={() => {
                                const newDate = new Date(day);
                                newDate.setHours(slot.hour, slot.minute, 0, 0);
                                setPrefilledDate(newDate);
                                setEditingBooking(null);
                                setShowDialog(true);
                              }}
                              className="w-full h-full min-h-[40px] text-xs text-gray-300 dark:text-gray-700 hover:text-brand-500 hover:bg-gray-100 dark:hover:bg-slate-700 rounded transition-colors"
                            >
                              +
                            </button>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* 多員工視圖 */}
      {viewMode === 'staff' && (
        <div className="bg-white dark:bg-slate-800 shadow rounded-lg overflow-hidden">
          {staff.length === 0 ? (
            <div className="p-8 text-center text-gray-500 dark:text-gray-400">
              {t('pages:admin.calendar.noStaffData')}
            </div>
          ) : (
            <>
              {/* 手機版：按員工分組的預約列表 */}
              <div className="md:hidden">
                {staff.filter(s => s.isActive).map((member) => {
                  const memberBookings = todayBookings.filter(b => b.staffId === member.id);
                  return (
                    <div key={member.id} className="border-b border-gray-200 dark:border-slate-700 last:border-0">
                      <div className="p-4 bg-gray-50 dark:bg-slate-700/50">
                        <h3 className="font-semibold text-gray-900 dark:text-white">
                          {member.name}
                          <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">
                            ({memberBookings.length} 筆)
                          </span>
                        </h3>
                        {member.phone && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{member.phone}</p>
                        )}
                      </div>
                      {memberBookings.length === 0 ? (
                        <div className="p-4 text-center text-sm text-gray-500 dark:text-gray-400">
                          {t('pages:admin.calendar.noBookingsToday')}
                        </div>
                      ) : (
                        <div className="divide-y divide-gray-200 dark:divide-gray-700">
                          {memberBookings.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()).map((booking) => (
                            <button
                              key={booking.id}
                              onClick={() => handleEditBooking(booking)}
                              className={`w-full text-left p-3 hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors ${getStatusColor(booking.status)} border-l-4`}
                            >
                              <div className="flex items-start justify-between gap-2">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
                                      {new Date(booking.date).toLocaleTimeString('zh-TW', { hour: '2-digit', minute: '2-digit' })}
                                    </span>
                                    <span className="text-xs font-bold text-brand-600 dark:text-brand-400">
                                      ${Math.floor(Number(booking.price))}
                                    </span>
                                  </div>
                                  <p className="font-semibold text-sm text-gray-900 dark:text-white mt-1 truncate">
                                    {booking.customer.name}
                                  </p>
                                  <p className="text-xs text-gray-600 dark:text-gray-400 truncate">
                                    {booking.service.name}
                                  </p>
                                </div>
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* 桌面版：完整多員工表格 */}
              <div className="hidden md:block overflow-x-auto scrollbar-hide">
                <table className="w-full" style={{ minWidth: `${200 + staff.length * 180}px` }}>
                <thead>
                  <tr className="border-b border-gray-200 dark:border-slate-700">
                    <th className="p-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 w-20 sticky left-0 bg-white dark:bg-slate-800 z-10">{t('translation:common.timeLabel')}</th>
                    {staff.filter(s => s.isActive).map((member) => (
                      <th key={member.id} className="p-3 text-center text-xs font-medium text-gray-700 dark:text-gray-300 w-44">
                        <div className="font-semibold">{member.name}</div>
                        {member.phone && <div className="text-gray-500 dark:text-gray-400 text-xs mt-1">{member.phone}</div>}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {timeSlots.map((slot) => (
                    <tr key={slot.time} className="hover:bg-gray-50 dark:hover:bg-slate-700/50">
                      <td className="p-2 text-xs text-gray-600 dark:text-gray-400 font-medium border-r border-gray-200 dark:border-slate-700 sticky left-0 bg-white dark:bg-slate-800">
                        {slot.time}
                      </td>
                      {staff.filter(s => s.isActive).map((member) => {
                        const staffBookings = todayBookings.filter(b =>
                          b.staffId === member.id &&
                          new Date(b.date).getHours() === slot.hour &&
                          new Date(b.date).getMinutes() === slot.minute
                        );
                        const booking = staffBookings[0];

                        return (
                          <td key={member.id} className="p-1">
                            {booking ? (
                              <button
                                onClick={() => handleEditBooking(booking)}
                                className={`w-full text-xs p-2 rounded ${getStatusColor(booking.status)} hover:shadow-md transition-shadow cursor-pointer`}
                              >
                                <div className="truncate font-semibold">{booking.customer.name}</div>
                                <div className="truncate text-xs opacity-75 mt-1">{booking.service.name}</div>
                                <div className="text-xs font-bold mt-1">${Math.floor(Number(booking.price))}</div>
                              </button>
                            ) : (
                              <button
                                onClick={() => {
                                  const newDate = new Date(selectedDate);
                                  newDate.setHours(slot.hour, slot.minute, 0, 0);
                                  setPrefilledDate(newDate);
                                  setEditingBooking(null);
                                  setShowDialog(true);
                                }}
                                className="w-full h-full min-h-[60px] text-xs text-gray-300 dark:text-gray-700 hover:text-brand-500 hover:bg-gray-100 dark:hover:bg-slate-700 rounded transition-colors"
                              >
                                +
                              </button>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            </>
          )}
        </div>
      )}

      {/* FAB 新增按鈕 */}
      <button
        onClick={() => handleAddBooking()}
        className="md:hidden fixed bottom-20 right-6 w-14 h-14 bg-brand-600 hover:bg-brand-700 text-white rounded-full shadow-lg flex items-center justify-center z-40 transition-all duration-200 active:scale-95"
        aria-label={t('translation:common.add')}
      >
        <PlusIcon className="w-6 h-6" />
      </button>

      {/* 新增/編輯預約對話框 */}
      <BookingDialog
        isOpen={showDialog}
        onClose={handleDialogClose}
        onSubmit={handleDialogSubmit}
        booking={editingBooking || (prefilledDate ? { date: prefilledDate.toISOString() } as any : null)}
      />
    </div>
  );
};

export default CalendarPage;
